import * as React from 'react';
import { useMemo, useState } from 'react';
import * as R from 'ramda';
import LocalDiv from '../../localization/LocalDiv';
import { FLEX_ROW, FLEX_COLUMN } from '../../layout/flexBox';
import { COLORS } from '../../globalStyle/colors';
import { css } from '@emotion/core';
import { FeedbackSurveyQuestion, CompetencyAndQuestion } from './models';
import { DevPlanItem } from '../devPlan/models';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWrench, faMinusSquare, faPlusSquare } from '@fortawesome/free-solid-svg-icons';
import ActionIcon from '../../layout/ActionIcon';
import { Z_INDEX } from '../../globalStyle/zIndex';
import { useAppState } from '../../core/redux/hooks';
import { useMsgText } from '../../localization/hooks';
import { Str } from '../../localization/translationKeys';


const styles = {
  container: css`
    border: 1px solid ${COLORS.WHITE};
    border-bottom: none;
  `,
  competency: css`
    justify-content: space-between;
    padding: 0 14px;
    min-height: 28px;
    border-bottom: 1px solid ${COLORS.WHITE};
  `,
  hide: css`
    display: none;
  `,
  question: css`
    justify-content: space-between;
    padding: 0 14px;
    min-height: 28px;
    border-bottom: 1px solid ${COLORS.WHITE};
  `,
  questionText: css`
    padding: 7px 26px;
  `,
  wrenchIcon: css`
    color: ${COLORS.DARK_GREY};
    margin-top: 7px;
  `,
  checkbox:      css`
    width: 22px;
    height: 22px;
  `,
  competencyOpen: css `
    margin-top: 5px;
  `,
  competencyName: css `
    margin: 7px 10px 0;
  `,
  selected: css `
    color: ${COLORS.MEDIUM_GREY};
  `,
  plusMinus: css`
    font-size: 1.1em;
    color: ${COLORS.DARK_GREY};
    cursor: pointer;
    text-align: center;
  `,
  italicized: css`
    font-style: italic;
  `,
};


type Props = {
  className?: string;
  allQuestions: CompetencyAndQuestion[];
  selectedQuestions: FeedbackSurveyQuestion[];
  devItems: DevPlanItem[];
  addByCompetency: boolean;
  checkboxOnClick: (question: CompetencyAndQuestion) => void;
};


/**
 * CreateSurveyAddAccordian: a component that shows subset of questions
 * PureComponent: true
 * Redux Connected: false
 */
const CreateSurveyAddAccordian: React.FC<Props> = props => {
  const competencies = Array.from(new Set(props.allQuestions.map((item: CompetencyAndQuestion) => item.competencyId)));
  const [open, setOpen] = useState<number | undefined>(undefined);
  const menuOpen = useAppState(state => state.openSideMenu);

  const expandLabel   = useMsgText(Str.Expand);
  const collapseLabel = useMsgText(Str.Collapse);

  const accordian = useMemo(
    () => {
      const showWrench = (competencyId: number) => (
        competencyId > 0 && props.devItems.some(x => x.devItemCompetencyId === competencyId)
      );

      const questionSelected = (questionId: number) => (
        props.selectedQuestions.some(x => x.questionId === questionId)
      );

      const competencySelected = (competencyId: number) => (
        props.selectedQuestions.some(x => x.competencyId === competencyId)
      );

      const openStyle = (competencyId: number) => (
        competencyId === 0 ? styles.italicized : undefined
      );

      const competency = (competencyId: number) => {
        const question = props.allQuestions.find(q => q.competencyId === competencyId);

        return(
          <LocalDiv key={competencyId} css={[FLEX_ROW, styles.competency]}>
            <LocalDiv css={FLEX_COLUMN}>
              <LocalDiv css={FLEX_ROW}>
                <ActionIcon
                  css={
                    [FLEX_COLUMN, styles.competencyOpen, css`z-index: ${menuOpen ? 'auto' : Z_INDEX.ABOVE_SIDE_MENU}`]
                  }
                  onClick={
                    open === competencyId
                      ? () => setOpen(undefined)
                      : () => setOpen(competencyId)
                  }
                  icon={open === competencyId ? faMinusSquare : faPlusSquare}
                  iconStyle={styles.plusMinus}
                  type='button'
                  aria-label={open === competencyId ? collapseLabel : expandLabel}
                />
                <span css={[
                  FLEX_COLUMN,
                  styles.competencyName,
                  props.addByCompetency && competencySelected(competencyId)
                    ? styles.selected
                    : undefined,
                  openStyle(competencyId),
                ]}>
                  {question?.name}
                </span>
                {
                  showWrench(competencyId || 0) && (
                    <FontAwesomeIcon
                      icon={faWrench}
                      css={[FLEX_COLUMN, styles.wrenchIcon]}
                      size={'1x'}
                    />
                  )
                }
              </LocalDiv>
            </LocalDiv>
            {
              props.addByCompetency && !competencySelected(competencyId) && (
                <LocalDiv css={FLEX_COLUMN}>
                  <input
                    type='checkbox'
                    css={[FLEX_COLUMN, styles.checkbox]}
                    onClick={() => {if (!!question) { props.checkboxOnClick(question)}}}
                  />
                </LocalDiv>
              )
            }
          </LocalDiv>
        )
      };

      const questions = (competencyId: number) => {
        const questionsForCompetency = props.allQuestions.filter(q => q.competencyId === competencyId)

        return (
          <LocalDiv
            css={open !== competencyId && styles.hide}
          >
            {questionMap(questionsForCompetency)}
          </LocalDiv>
        )
      };

      const questionKey = (question: CompetencyAndQuestion) => (
        `${question.competencyId}|${question.questionId}`
      );

      const questionMap = (questionsForCompetency: CompetencyAndQuestion[]) => (
        R.map(
          (question: CompetencyAndQuestion) => (
            <LocalDiv key={questionKey(question)} css={[FLEX_ROW, styles.question]}>
              <LocalDiv css={[
                FLEX_COLUMN,
                styles.questionText,
                !props.addByCompetency && questionSelected(question.questionId) ? styles.selected : undefined,
              ]}>
                {question.questionText}
              </LocalDiv>
              {
                !props.addByCompetency && !questionSelected(question.questionId) && (
                  <LocalDiv css={FLEX_COLUMN}>
                    <input
                      type='checkbox'
                      css={[FLEX_COLUMN, styles.checkbox]}
                      onClick={() =>props.checkboxOnClick(question)}
                    />
                  </LocalDiv>
                )
              }
            </LocalDiv>
          ),
          questionsForCompetency,
        )
      );

      return R.map(
        (competencyId: number) => (
          <LocalDiv key={`|${competencyId}|`}>
            {competency(competencyId)}
            {questions(competencyId)}
          </LocalDiv>
        ),
        competencies,
      )
    },
    [
      competencies,
      open,
      props,
      menuOpen,
      expandLabel,
      collapseLabel,
    ],
  );

  return (
    <LocalDiv css={styles.container}>
      {accordian}
    </LocalDiv>
  );
};

export default CreateSurveyAddAccordian;
